import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
} from '@mui/material';
import {
    Home as HomeIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { t } from 'i18next';
import calculateNights from '@shared/utils/calculateNights';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Backdrop from "@mui/material/Backdrop";
import { CircularProgress } from "@mui/material";
import { formatDateWithIntl } from '@shared/utils/getFormatDate';
import { STEPS, getTomorrowDate } from '../constants';
import { getStatusChipMeta } from '../../../lib/reservationRules';

const RenderStepContent = ({
    step,
    reservationData,
    setReservationData,
    validateFields,
    error,
    fieldErrors,
    setFieldErrors,
    isEditMode,
    vacationHomes,
    availableRooms,
    setError,
    checkAvailability,
    maxGuestsAllowed,
    validateDates,
    setAvailableRooms,
    isCheckingAvailability,
    setIsCheckingAvailability,
}) => {
    const selectedLocation = vacationHomes.find(
        (location) => location.name === reservationData.location
    );

    switch (step) {
        case STEPS.SELECT_LOCATION:
            return (
                <Box>
                    <Typography variant="h6" gutterBottom>
                        {t('selectLocation')}:
                    </Typography>
                    <Grid container spacing={2}>
                        {vacationHomes.map((location) => (
                            <Grid
                                key={location.name}
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <Card
                                    onClick={() => setReservationData({ ...reservationData, location: location.name })}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '16px',
                                        border: (theme) =>
                                            reservationData.location === location.name
                                                ? `2px solid ${theme.palette.primary.main}`
                                                : `1px solid ${theme.palette.divider}`,
                                        boxShadow: reservationData.location === location.name
                                                                    ? '0 4px 12px rgba(25,118,210,0.4)'
                                                                    : '0 2px 6px rgba(0,0,0,0.05)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 6px 14px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'grey.100',
                                            gap: 1.5,
                                            p: 2,
                                            borderTopLeftRadius: '16px',
                                            borderTopRightRadius: '16px'
                                        }}
                                    >
                                        <HomeIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                                        <Typography variant="h6" align="center" sx={{fontWeight: "bold"}}>
                                            {location.name}
                                        </Typography>
                                    </Box>
                                    <CardContent>
                                        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
                                            {location.description}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", mb: 2 }}>
                                            {t('conditions')}
                                        </Typography>
                                        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                            <Box
                                                component="li"
                                                sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                            >
                                                <CheckCircleIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                                <Typography variant="body2">
                                                    {t('minimumNightsRequired', { minNights: location.minimumStay })}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="li"
                                                sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                            >
                                                <CheckCircleIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                                <Typography variant="body2">
                                                    {t('maximumDaysNoGuests', {
                                                        maxDaysWithOutGuests: location.maximumStayWithoutGuests
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="li"
                                                sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                                            >
                                                <CheckCircleIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                                                <Typography variant="body2">
                                                    {t('maximumDaysWithGuests', {
                                                        maxDaysWithGuests: location.maximumStayWithGuests
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    {(validateFields && !reservationData.location) ? (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />
                            <Typography color="error" variant={"body2"}>
                                {validateFields && !reservationData.location ? t('errorSelectLocation') : ''}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            );

        case STEPS.SELECT_DATES:
            return (
                <Box>
                    <Typography variant="h6">{t('selectDatesAndGuests')}:</Typography>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6
                            }}>
                            <TextField
                                label={t('selectStartDate')}
                                type="date"
                                fullWidth
                                value={reservationData.startDate}
                                onChange={(e) => {
                                    setReservationData({
                                        ...reservationData,
                                        startDate: e.target.value,
                                        isAvailabilityChecked: false,
                                    });
                                    if (fieldErrors.startDate) {
                                        setFieldErrors({ ...fieldErrors, startDate: '' });
                                    }
                                    setError('');
                                    setAvailableRooms([]);
                                }}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    htmlInput: { min: getTomorrowDate() },
                                }}
                                sx={{ mt: 2 }}
                                required
                                error={Boolean(fieldErrors.startDate)}
                                helperText={fieldErrors.startDate}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6
                            }}>
                            <TextField
                                label={t('selectEndDate')}
                                type="date"
                                fullWidth
                                value={reservationData.endDate}
                                onChange={(e) => {
                                    setReservationData({
                                        ...reservationData,
                                        endDate: e.target.value,
                                        isAvailabilityChecked: false,
                                    });
                                    if (fieldErrors.endDate) {
                                        setFieldErrors({ ...fieldErrors, endDate: '' });
                                    }
                                    setError('');
                                    setAvailableRooms([]);
                                }}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    htmlInput: {
                                        min: reservationData.startDate
                                            ? new Date(reservationData.startDate).toISOString().split('T')[0]
                                            : getTomorrowDate(),
                                    },
                                }}
                                sx={{ mt: 2 }}
                                required
                                error={Boolean(fieldErrors.endDate)}
                                helperText={fieldErrors.endDate}
                            />
                        </Grid>

                        {(isEditMode || reservationData.isAvailabilityChecked) && (
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6
                                }}>
                                <TextField
                                    label={t('numberOfGuests')}
                                    type="number"
                                    fullWidth
                                    value={reservationData.numberOfGuests}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        if (value >= 0 && value <= maxGuestsAllowed) {
                                            setReservationData({ ...reservationData, numberOfGuests: value });
                                        }
                                    }}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        htmlInput: { min: 0, max: maxGuestsAllowed },
                                    }}
                                    required
                                    helperText={fieldErrors.numberOfGuests || t('maxGuestsAllowed', { maxGuests: maxGuestsAllowed })}
                                    error={Boolean(fieldErrors.numberOfGuests)}
                                />
                            </Grid>
                        )}

                        <Grid size={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    setError('');
                                    const isValid = await validateDates();
                                    if (!isValid) {
                                        return;
                                    }
                                    setIsCheckingAvailability(true);
                                    const hasAvailability = await checkAvailability();
                                    setIsCheckingAvailability(false);
                                    if (!hasAvailability) {
                                        return;
                                    }
                                }}
                                disabled={!reservationData.startDate || !reservationData.endDate}
                            >
                                {isCheckingAvailability ? (
                                    <>
                                        <CircularProgress size={24} color="inherit" />
                                        &nbsp;
                                        {t('checkingAvailability')}
                                    </>
                                ) : (
                                    t('checkAvailability')
                                )}
                            </Button>

                            {reservationData.isAvailabilityChecked && selectedLocation.maximumGuests > 0 && (
                                <Typography variant="body2" sx={{ mt: 2 }}>
                                    {t('availableRoomsCount', { count: selectedLocation.availableRooms })}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                    {error && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        </Box>
                    )}
                    <Backdrop open={isCheckingAvailability} style={{ zIndex: 9999 }}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </Box>
            );

        case STEPS.SELECT_ROOMS:
            return (
                <Box>
                    <Typography variant="h6">{t('selectRooms')}:</Typography>
                    {(availableRooms.length > 0 || isEditMode) ? (
                        <TextField
                            label={t('numberOfRooms')}
                            type="number"
                            fullWidth
                            value={reservationData.numberOfRooms}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (value >= 1 && value <= availableRooms.length) {
                                    setReservationData({
                                        ...reservationData,
                                        numberOfRooms: value,
                                    });
                                } else if (e.target.value === '') {
                                    setReservationData({ ...reservationData, numberOfRooms: 0 });
                                }
                            }}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: { min: 1, max: availableRooms.length },
                            }}
                            sx={{ mt: 2 }}
                            required
                            error={
                                validateFields && !reservationData.numberOfRooms
                            }
                            helperText={
                                validateFields && !reservationData.numberOfRooms
                                    ? t('errorNumberOfRooms')
                                    : t('availableRoomsCount', { count: availableRooms.length })
                            }
                        />
                    ) : (
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {t('noRoomsAvailable')}
                        </Typography>
                    )}
                    {error && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <ErrorOutlineIcon color="error" sx={{ mr: 1 }} />
                            <Typography color="error" variant={"body2"}>
                                {error}
                            </Typography>
                        </Box>
                    )}
                </Box>
            );

        case STEPS.CONFIRMATION:
            const nights = calculateNights(
                reservationData.startDate,
                reservationData.endDate
            );

            return (
                    <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', p: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('location')}:</Typography>
                                    <Typography variant="body1">{selectedLocation ? selectedLocation.name : t('unknown')}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('startDate')}:</Typography>
                                    <Typography variant="body1">{formatDateWithIntl(reservationData.startDate, 'medium')}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('endDate')}:</Typography>
                                    <Typography variant="body1">{formatDateWithIntl(reservationData.endDate, 'medium')}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('numberOfGuests')}:</Typography>
                                    <Typography variant="body1">{reservationData.numberOfGuests}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('numberOfRooms')}:</Typography>
                                    <Typography variant="body1">{reservationData.numberOfRooms}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('totalNights')}:</Typography>
                                    <Typography variant="body1">{nights}</Typography>
                                </Box>
                                {isEditMode && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body1" sx={{fontWeight: "bold"}}>{t('status')}:</Typography>
                                        <Typography variant="body1">{t(getStatusChipMeta(reservationData.status).labelKey)}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                );

        default:
            return t('unknownStep');
    }
};

export default RenderStepContent;
